import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { MemoraiButton } from './MemoraiButton.js';
import { MemoraiCard, MemoraiCardContent } from './MemoraiCard.js';

interface Props {
    children?: ReactNode;
    title?: string;
    description?: string;
    reloadLabel?: string;
    technicalDetailsLabel?: string;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class MemoraiErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
    }

    private handleReset = () => {
        this.setState({ hasError: false, error: null });
        window.location.href = '/';
    };

    public render() {
        if (this.state.hasError) {
            const {
                title = 'Something went wrong',
                description = 'The application encountered an unexpected error.',
                reloadLabel = 'Restart Application',
                technicalDetailsLabel = 'Technical Details',
            } = this.props;

            return (
                <div className="min-h-dvh w-full flex items-center justify-center p-6 bg-background">
                    <MemoraiCard className="w-full max-w-md border-rose-500/20 bg-rose-500/5 rounded-[2.5rem] overflow-hidden shadow-2xl shadow-rose-500/5">
                        <MemoraiCardContent className="p-8 flex flex-col items-center text-center space-y-6">
                            <div className="bg-rose-500 p-4 rounded-2xl shadow-lg shadow-rose-500/20">
                                <AlertTriangle className="w-8 h-8 text-white" />
                            </div>

                            <div className="space-y-2">
                                <h1 className="text-2xl font-bold text-foreground">{title}</h1>
                                <p className="text-foreground-subtle text-sm">
                                    {description}
                                </p>
                            </div>

                            {this.state.error && (
                                <div className="w-full p-4 bg-background-subtle rounded-xl border border-border/50 text-left">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-foreground-subtle mb-2">
                                        {technicalDetailsLabel}
                                    </p>
                                    <p className="text-xs font-mono text-rose-500 break-words leading-relaxed">
                                        {this.state.error.message}
                                    </p>
                                </div>
                            )}

                            <MemoraiButton
                                onClick={this.handleReset}
                                className="w-full flex items-center justify-center gap-2 py-6 rounded-2xl"
                            >
                                <RotateCcw className="w-4 h-4" />
                                {reloadLabel}
                            </MemoraiButton>
                        </MemoraiCardContent>
                    </MemoraiCard>
                </div>
            );
        }

        return this.props.children;
    }
}
